import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckIcon, EllipsisIcon, PackageIcon, PencilIcon, PlusIcon, PrinterIcon, RotateCcwIcon, TruckIcon, XIcon } from 'lucide-react';
import { BackLink, Button, CardHeader, Combobox, ConfirmDialog, DataTable, EmptyState, ErrorNotice, Field, LoadingState, Pagination, Panel, Pill, SearchInput, SkeletonTable, TextArea, TextInput, type DataTableSort } from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage } from '@/lib/use-list-query-state';
import { cn } from '@/lib/utils';
import { listEventShipments, readEvent, readMyIdentity, type Shipment } from '@/lib/events';
import { addPackingLine, deletePackingLine, listPackingList, listSupplyItems, packingLineText, resetPackingList, shipmentLabel, updatePackingLine, vendorsText, type PackingLine, type PackingShipment } from '@/lib/supplies';

const SORT_FIELDS = { name: 'name', quantity: 'quantity', status: 'statusOrder' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.name, direction: 'asc' };
const PACKING_PAGE_SIZE = 25;

interface PackingRow extends PackingLine {
  readonly statusOrder: number;
  readonly vendorText: string;
}
function packingRow(line: PackingLine): PackingRow {
  return { ...line, statusOrder: line.packedAt ? 2 : line.clientProvides ? 1 : 0, vendorText: vendorsText(line.sources) };
}

const MANAGER_ROLES = new Set(['admin', 'superadmin', 'internal']);

/**
 * The Materials tab: the event's packing list, built from the program's
 * kit and edited here. Internal roles edit; facilitators read.
 */
export function MaterialsTab({ eventId }: { readonly eventId: string }): React.ReactElement {
  const me = useQuery({ queryKey: ['staff-me'], queryFn: ({ signal }) => readMyIdentity(signal) });
  return <PackingList eventId={eventId} canEdit={MANAGER_ROLES.has(me.data?.role ?? '')} />;
}

/**
 * The packing list as a list page: a header with the count and actions, a
 * search box, then the table at full width. Quantity, note and who
 * provides an item are edited in a dialog; packed is a tick on the row.
 */
function PackingList({ eventId, canEdit }: { readonly eventId: string; readonly canEdit: boolean }): React.ReactElement {
  const queryClient = useQueryClient();
  const queryKey = ['packing-list', eventId];
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<PackingLine | null>(null);
  const [shipping, setShipping] = useState<PackingLine | null>(null);
  const [removing, setRemoving] = useState<PackingLine | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const { page, setPage } = useUrlPage();
  const list = useQuery({ queryKey, queryFn: ({ signal }) => listPackingList(eventId, signal) });
  const shipments = useQuery({ queryKey: ['event-shipments', eventId], queryFn: ({ signal }) => listEventShipments(eventId, signal) });
  const shipmentById = new Map((shipments.data ?? []).map((shipment) => [shipment._id, shipment]));
  const apply = (rows: readonly PackingLine[]): void => {
    queryClient.setQueryData(queryKey, rows);
  };
  const patch = useMutation({
    mutationFn: (input: { line: PackingLine; body: Parameters<typeof updatePackingLine>[2] }) => updatePackingLine(eventId, input.line._id, input.body),
    onSuccess: apply,
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (line: PackingLine) => deletePackingLine(eventId, line._id),
    onSuccess: (rows, line) => {
      apply(rows);
      toast.success(`Removed ${line.name}`);
      setRemoving(null);
    },
  });
  const reset = useMutation({
    mutationFn: () => resetPackingList(eventId),
    onSuccess: (rows) => {
      apply(rows);
      setRebuilding(false);
      toast.success('Rebuilt from the program');
    },
  });

  if (list.isPending) return <SkeletonTable label="Loading packing list…" />;
  if (list.isError) return <ErrorNotice title="Packing list unavailable" message={errorMessage(list.error)} />;
  const rows = sortRows(filterRows(list.data.map(packingRow), search, ['name', 'note', 'vendorText']), sort);
  const totalPages = Math.max(1, Math.ceil(rows.length / PACKING_PAGE_SIZE));
  const visible = rows.slice((page - 1) * PACKING_PAGE_SIZE, page * PACKING_PAGE_SIZE);
  const packed = list.data.filter((line) => line.packedAt).length;
  const toPack = list.data.filter((line) => !line.clientProvides).length;
  const missingCounts = list.data.filter((line) => line.needsHeadcount).length;

  return (
    <div className="mt-6">
      <div data-guide="materials-header">
      <CardHeader title="Packing list" description={list.data.length === 0 ? 'Built from the program kit when there is one.' : `${packed} of ${toPack} packed${missingCounts ? ` · ${missingCounts} need a team count` : ''}`} />
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {list.data.length > 0 ? (
          <SearchInput
            data-guide="materials-search"
            label="Search packing list"
            placeholder="Item, note or vendor"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="w-full max-w-sm"
          />
        ) : null}
        <span className="ml-auto flex flex-wrap items-center gap-2">
          <span data-guide="materials-print">
            <Button variant="secondary" size="compact" to={`/events/${eventId}/packing-list/print`}>
              <PrinterIcon aria-hidden className="size-4" /> Print
            </Button>
          </span>
          {canEdit ? (
            <>
              <span data-guide="materials-rebuild">
                <Button variant="secondary" size="compact" onClick={() => setRebuilding(true)}>
                  <RotateCcwIcon aria-hidden className="size-4" /> Rebuild from program
                </Button>
              </span>
              <span data-guide="materials-add">
                <Button size="compact" onClick={() => setAdding(true)}>
                  <PlusIcon aria-hidden className="size-4" /> Add item
                </Button>
              </span>
            </>
          ) : null}
        </span>
      </div>
      {list.data.length === 0 ? (
        <Panel>
          <EmptyState icon={<PackageIcon aria-hidden className="size-6" />} title="Nothing to pack yet" message="Give the program a kit under Programs, or add items here for this event." {...(canEdit ? { action: <Button onClick={() => setAdding(true)}>Add item</Button> } : {})} />
        </Panel>
      ) : (
        <div data-guide="materials-table">
          <DataTable headers={['Packed', { label: 'Item', sortKey: SORT_FIELDS.name }, { label: 'Qty', sortKey: SORT_FIELDS.quantity }, { label: 'Status', sortKey: SORT_FIELDS.status }, 'Order', 'Shipped in', 'Actions']} visuallyHiddenHeaders={[6]} rightAlignedHeaders={[2]} sort={sort} onSort={onSort} minWidth={900}>
            {visible.map((line) => (
              <tr key={line._id} className={cn('border-t border-portal-line align-middle', line.clientProvides && 'bg-portal-bg/60')}>
                <td className="p-3">
                  <button
                    type="button"
                    aria-pressed={Boolean(line.packedAt)}
                    aria-label={line.packedAt ? `Mark "${line.name}" not packed` : `Mark "${line.name}" packed`}
                    disabled={!canEdit || line.clientProvides || patch.isPending}
                    onClick={() => patch.mutate({ line, body: { packed: !line.packedAt } })}
                    className={cn('mt-0.5 grid size-4 place-items-center rounded-[5px] border transition-colors', line.clientProvides ? 'cursor-not-allowed border-portal-line' : line.packedAt ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-portal-line hover:border-emerald-500')}
                  >
                    {line.packedAt ? <CheckIcon aria-hidden className="size-3" /> : null}
                  </button>
                </td>
                <td className="p-3 text-portal-small">
                  <span className="flex items-center gap-3">
                    {line.imageUrl ? (
                      <img src={line.imageUrl} alt="" className="size-10 shrink-0 rounded-md border border-portal-line object-cover" />
                    ) : (
                      <span aria-hidden className="grid size-10 shrink-0 place-items-center rounded-md border border-portal-line bg-portal-bg text-portal-muted">
                        <PackageIcon className="size-4" />
                      </span>
                    )}
                    <span className="min-w-0">
                      <span className={cn('block font-bold', line.clientProvides && 'text-portal-muted line-through')}>{line.name}</span>
                      <span className="block text-portal-micro text-portal-muted">
                        {[line.note, line.source === 'manual' ? 'Added for this event' : null, line.optional ? 'Optional' : null].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </span>
                </td>
                <td className="p-3 text-right text-portal-small tabular-nums whitespace-nowrap">
                  {line.quantity}
                  {line.unit ? <span className="ml-1 text-portal-muted">{line.unit}</span> : null}
                </td>
                <td className="p-3 text-portal-small whitespace-nowrap">
                  {line.clientProvides ? <Pill tone="neutral">Client provides</Pill> : line.needsHeadcount ? <Pill tone="warning">Needs team count</Pill> : line.packedAt ? <Pill tone="success">Packed</Pill> : <Pill tone="brand">To pack</Pill>}
                </td>
                <td className="p-3 text-portal-small">
                  {!line.sources?.length ? (
                    <span className="text-portal-muted">—</span>
                  ) : (
                    <span className="flex flex-wrap gap-x-2 gap-y-0.5">
                      {line.sources.map((source, index) =>
                        source.url ? (
                          <a key={`${source.vendor}-${index}`} href={source.url} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                            {source.vendor}
                          </a>
                        ) : (
                          <span key={`${source.vendor}-${index}`} className="text-portal-muted">{source.vendor}</span>
                        ),
                      )}
                    </span>
                  )}
                </td>
                <td className="p-3 text-portal-small">
                  {!line.shipments?.length ? (
                    <span className="text-portal-muted">—</span>
                  ) : (
                    <span className="flex flex-col gap-0.5">
                      {line.shipments.map((entry) => {
                        const shipment = shipmentById.get(entry.shipmentId);
                        return (
                          <span key={entry.shipmentId} className="whitespace-nowrap">
                            {shipment ? (
                              <Link to={`/shipments/${entry.shipmentId}`} className="text-brand hover:underline">
                                {shipmentLabel(shipment)}
                              </Link>
                            ) : (
                              <span className="text-portal-muted">Shipment no longer on the event</span>
                            )}
                            {entry.quantity !== undefined && entry.quantity !== line.quantity ? <span className="ml-1 text-portal-micro text-portal-muted">({entry.quantity})</span> : null}
                          </span>
                        );
                      })}
                    </span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {canEdit ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" data-guide="materials-row-menu" aria-label={`Actions for ${line.name}`} className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink">
                          <EllipsisIcon aria-hidden className="size-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-guide="materials-menu-edit" onSelect={() => setEditing(line)}>
                          <PencilIcon aria-hidden className="size-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem data-guide="materials-menu-ship" onSelect={() => setShipping(line)}>
                          <TruckIcon aria-hidden className="size-4" /> Shipped in…
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" onSelect={() => setRemoving(line)}>
                          <XIcon aria-hidden className="size-4" /> Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </td>
              </tr>
            ))}
          </DataTable>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} summary={`${rows.length} item${rows.length === 1 ? '' : 's'}`} />
        </div>
      )}
      {adding ? (
        <AddLineDialog
          eventId={eventId}
          onClose={() => setAdding(false)}
          onSaved={async (rows) => {
            apply(rows);
            setAdding(false);
          }}
        />
      ) : null}
      {shipping ? (
        <ShipInDialog
          key={shipping._id}
          eventId={eventId}
          line={shipping}
          shipments={shipments.data ?? []}
          onClose={() => setShipping(null)}
          onSaved={(rows) => {
            apply(rows);
            setShipping(null);
          }}
        />
      ) : null}
      {editing ? (
        <EditLineDialog
          key={editing._id}
          eventId={eventId}
          line={editing}
          onClose={() => setEditing(null)}
          onSaved={(rows) => {
            apply(rows);
            setEditing(null);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={rebuilding}
        tone="default"
        title="Rebuild the packing list from the program?"
        message="Every line that came from the program kit is replaced with a fresh copy at today's headcount, and its packed ticks and edits are lost. Items added for this event stay."
        confirmLabel="Rebuild"
        pending={reset.isPending}
        error={reset.isError ? errorMessage(reset.error) : undefined}
        onConfirm={() => reset.mutate()}
        onCancel={() => {
          if (!reset.isPending) setRebuilding(false);
        }}
      />
      <ConfirmDialog
        open={removing !== null}
        tone="danger"
        title={`Remove "${removing?.name ?? 'this item'}"?`}
        message="It comes off this event's list only. Rebuilding from the program brings kit items back."
        confirmLabel="Remove"
        pending={remove.isPending}
        error={remove.isError ? errorMessage(remove.error) : undefined}
        onConfirm={() => {
          if (removing) remove.mutate(removing);
        }}
        onCancel={() => {
          if (!remove.isPending) setRemoving(null);
        }}
      />
    </div>
  );
}

/** Which of the event's shipments a line travels in, and how many go in each. */
function ShipInDialog({ eventId, line, shipments, onClose, onSaved }: { readonly eventId: string; readonly line: PackingLine; readonly shipments: readonly Shipment[]; readonly onClose: () => void; readonly onSaved: (rows: readonly PackingLine[]) => void }): React.ReactElement {
  const [chosen, setChosen] = useState<ReadonlyMap<string, string>>(new Map((line.shipments ?? []).map((entry) => [entry.shipmentId, entry.quantity === undefined ? '' : String(entry.quantity)])));
  const toggle = (shipmentId: string, on: boolean): void =>
    setChosen((current) => {
      const next = new Map(current);
      if (on) next.set(shipmentId, current.get(shipmentId) ?? '');
      else next.delete(shipmentId);
      return next;
    });
  const setQuantity = (shipmentId: string, value: string): void =>
    setChosen((current) => {
      const next = new Map(current);
      next.set(shipmentId, value);
      return next;
    });
  const entries: PackingShipment[] = [...chosen.entries()].map(([shipmentId, quantity]) => ({ shipmentId, ...(quantity.trim() === '' ? {} : { quantity: Number(quantity) }) }));
  const assigned = entries.reduce((sum, entry) => sum + (entry.quantity ?? 0), 0);
  const save = useMutation({
    mutationFn: () => updatePackingLine(eventId, line._id, { shipments: entries }),
    onSuccess: (rows) => {
      toast.success(entries.length ? `${line.name} ships in ${entries.length} ${entries.length === 1 ? 'shipment' : 'shipments'}` : `${line.name} is not in any shipment`);
      onSaved(rows);
    },
  });
  const valid = [...chosen.values()].every((value) => value.trim() === '' || Number(value) >= 0) && !save.isPending;
  return (
    <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="max-h-[calc(100vh-4rem)] overflow-y-auto sm:max-w-lg">
        <DialogTitle>Shipped in</DialogTitle>
        <p className="text-portal-small text-portal-muted">Tick every shipment "{line.name}" travels in. Split the quantity when it goes in more than one.</p>
        <form
          className="mt-4 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) save.mutate();
          }}
        >
          {shipments.length === 0 ? (
            <EmptyState
              icon={<TruckIcon aria-hidden className="size-6" />}
              title="No shipments yet"
              message="Add the event's shipments first, then come back to say which one carries this item."
              action={<Button to={`/events/${eventId}?tab=shipments`}>Go to shipments</Button>}
            />
          ) : (
            <ul className="divide-y divide-portal-line rounded-portal-button border border-portal-line">
              {shipments.map((shipment) => {
                const on = chosen.has(shipment._id);
                return (
                  <li key={shipment._id} className="flex flex-wrap items-center gap-3 px-3 py-2">
                    <label data-guide="materials-shipped-in-row" className="flex min-w-0 flex-1 items-center gap-2 text-portal-small">
                      <input type="checkbox" checked={on} onChange={(event) => toggle(shipment._id, event.target.checked)} />
                      <span className="min-w-0">
                        <span className="block font-bold">{shipmentLabel(shipment)}</span>
                        {shipment.status ? <span className="block text-portal-micro text-portal-muted">{shipment.status}</span> : null}
                      </span>
                    </label>
                    {on ? (
                      <span className="flex items-center gap-2 text-portal-small">
                        <label htmlFor={`ship-qty-${shipment._id}`} className="text-portal-muted">
                          Qty
                        </label>
                        <TextInput id={`ship-qty-${shipment._id}`} type="number" min={0} className="w-24" value={chosen.get(shipment._id) ?? ''} placeholder={String(line.quantity)} onChange={(event) => setQuantity(shipment._id, event.target.value)} />
                        {line.unit ? <span className="text-portal-muted">{line.unit}</span> : null}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
          {shipments.length > 0 ? (
            <p className="text-portal-micro text-portal-muted">
              {entries.some((entry) => entry.quantity !== undefined)
                ? `${assigned} of ${line.quantity}${line.unit ? ` ${line.unit}` : ''} assigned. An empty quantity means the whole line.`
                : 'Leave the quantity empty when the whole line goes in one shipment.'}
            </p>
          ) : null}
          {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            {shipments.length > 0 ? (
              <Button type="submit" disabled={!valid}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            ) : null}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditLineDialog({ eventId, line, onClose, onSaved }: { readonly eventId: string; readonly line: PackingLine; readonly onClose: () => void; readonly onSaved: (rows: readonly PackingLine[]) => void }): React.ReactElement {
  const [quantity, setQuantity] = useState(String(line.quantity));
  const [note, setNote] = useState(line.note ?? '');
  const [clientProvides, setClientProvides] = useState(line.clientProvides ?? false);
  const save = useMutation({
    mutationFn: () => updatePackingLine(eventId, line._id, { quantity: Number(quantity), note: note.trim(), clientProvides }),
    onSuccess: (rows) => {
      toast.success(`Saved ${line.name}`);
      onSaved(rows);
    },
  });
  const valid = quantity.trim() !== '' && Number(quantity) >= 0 && !save.isPending;
  return (
    <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle>Edit "{line.name}"</DialogTitle>
        <form
          className="mt-2 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) save.mutate();
          }}
        >
          <div className="max-w-[160px]">
            <Field guide="materials-edit-quantity" label="Quantity" htmlFor="edit-qty" required hint={line.unit ? `In ${line.unit}.` : 'How many to pack.'}>
              <TextInput id="edit-qty" type="number" min={0} value={quantity} onChange={(event) => setQuantity(event.target.value)} />
            </Field>
          </div>
          <Field guide="materials-edit-note" label="Note" htmlFor="edit-note" hint="Sizes, colors, who asked for it.">
            <TextArea id="edit-note" rows={3} value={note} maxLength={300} onChange={(event) => setNote(event.target.value)} />
          </Field>
          <label data-guide="materials-edit-client" className="flex items-center gap-2 text-portal-small">
            <input type="checkbox" checked={clientProvides} onChange={(event) => setClientProvides(event.target.checked)} />
            The client provides this, so we do not pack it
          </label>
          {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddLineDialog({ eventId, onClose, onSaved }: { readonly eventId: string; readonly onClose: () => void; readonly onSaved: (rows: readonly PackingLine[]) => Promise<void> }): React.ReactElement {
  const catalog = useQuery({ queryKey: ['supplies'], queryFn: ({ signal }) => listSupplyItems(signal), staleTime: 60_000 });
  const [itemId, setItemId] = useState('');
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => addPackingLine(eventId, { ...(itemId ? { itemId } : { name: name.trim() }), quantity: Number(quantity), note: note.trim() }),
    onSuccess: async (rows) => {
      toast.success('Added to the packing list');
      await onSaved(rows);
    },
  });
  const valid = (itemId || name.trim()) && Number(quantity) >= 0 && !save.isPending;
  return (
    <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle>Add to the packing list</DialogTitle>
        <form
          className="mt-2 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) save.mutate();
          }}
        >
          <Field guide="materials-add-item" label="Catalog item" htmlFor="pack-item" hint="Pick from the catalog, or leave it empty and name a one-off below.">
            <Combobox id="pack-item" options={(catalog.data ?? []).filter((item) => item.isActive).map((item) => ({ value: item._id, label: item.name }))} value={itemId} onChange={setItemId} placeholder="Search the catalog" noneLabel="One-off item" />
          </Field>
          {!itemId ? (
            <Field guide="materials-add-oneoff" label="One-off item" htmlFor="pack-name" required hint="Something for this event only. It never joins the catalog.">
              <TextInput id="pack-name" value={name} maxLength={160} placeholder="Printed signs from production" onChange={(event) => setName(event.target.value)} />
            </Field>
          ) : null}
          <div className="max-w-[160px]">
            <Field guide="materials-add-quantity" label="Quantity" htmlFor="pack-qty" required hint="How many to pack.">
              <TextInput id="pack-qty" type="number" min={0} value={quantity} onChange={(event) => setQuantity(event.target.value)} />
            </Field>
          </div>
          <Field guide="materials-add-note" label="Note" htmlFor="pack-note" hint="Sizes, colors, who asked for it.">
            <TextArea id="pack-note" rows={3} value={note} maxLength={300} onChange={(event) => setNote(event.target.value)} />
          </Field>
          {save.isError ? <ErrorNotice inline title="Not added" message={errorMessage(save.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid}>
              {save.isPending ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The printable packing list: one line per item, with a box to tick. */
export function PackingListPrintPage(): React.ReactElement {
  const { eventId } = useParams<{ eventId: string }>();
  const event = useQuery({ queryKey: ['event', eventId], queryFn: ({ signal }) => readEvent(eventId ?? '', signal), enabled: eventId !== undefined });
  const list = useQuery({ queryKey: ['packing-list', eventId], queryFn: ({ signal }) => listPackingList(eventId ?? '', signal), enabled: eventId !== undefined });
  if (event.isPending || list.isPending) return <LoadingState label="Preparing the packing list…" />;
  if (event.isError) return <ErrorNotice title="Event unavailable" message={errorMessage(event.error)} />;
  if (list.isError) return <ErrorNotice title="Packing list unavailable" message={errorMessage(list.error)} />;
  const toPack = list.data.filter((line) => !line.clientProvides);
  return (
    <section className="animate-portal-rise">
      <div className="print:hidden">
        <BackLink to={`/events/${eventId}?tab=materials`}>Back to materials</BackLink>
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-portal-small text-portal-muted">One line per item, with a box to tick as it goes in.</p>
          <Button onClick={() => window.print()}>
            <PrinterIcon aria-hidden className="size-4" /> Print
          </Button>
        </div>
      </div>
      <div className="mt-4 rounded-portal-card border border-portal-line bg-white p-6 print:border-0 print:p-0">
        <h1 className="text-portal-card-title font-bold">
          Packing list — {event.data.account?.name ?? event.data.opportunityName}
          {event.data.contractNumber ? ` (${event.data.contractNumber})` : ''}
        </h1>
        <p className="text-portal-small text-portal-muted">{event.data.programName ?? ''}{event.data.eventDate ? ` · ${new Date(event.data.eventDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}` : ''}</p>
        <ol className="mt-4 space-y-1 text-portal-small">
          {toPack.map((line) => (
            <li key={line._id} className="flex items-baseline gap-2">
              <span className="inline-block size-3 shrink-0 self-center border border-portal-ink" aria-hidden />
              <span>
                {packingLineText(line)}
                {line.note ? <span className="text-portal-muted"> — {line.note}</span> : null}
              </span>
            </li>
          ))}
        </ol>
        {list.data.some((line) => line.clientProvides) ? (
          <p className="mt-3 text-portal-micro text-portal-muted">Client provides: {list.data.filter((line) => line.clientProvides).map((line) => line.name).join(', ')}</p>
        ) : null}
      </div>
    </section>
  );
}
